<template>
  <OnClickOutside
    :options="{ ignore: ['.price-rule-sprite-card', '.terms-acceptance'] }"
    @trigger="dismiss"
  >
    <div
      ref="triggerEl"
      class="price-rule-sprite"
      :class="{ 'price-rule-sprite--interactive': showTooltip }"
      :style="rootStyle"
      :aria-describedby="isCardVisible ? cardId : undefined"
      @mouseenter="onHover(true)"
      @mouseleave="onHover(false)"
    >
      <img
        v-if="showIcon"
        class="price-rule-sprite__icon"
        :src="sprite.icon?.file.url"
        :alt="sprite.icon?.description || sprite.icon?.title || ''"
      />
      <span class="price-rule-sprite__label">{{ displayLabel }}</span>
    </div>

    <Teleport to="body">
      <div
        v-if="isCardVisible"
        :id="cardId"
        ref="cardEl"
        class="price-rule-sprite-card"
        :style="floatingStyles"
        role="tooltip"
      >
        <PriceRuleExplainerCard :markdown="sprite.extendedDescription || ''" />
      </div>
    </Teleport>
  </OnClickOutside>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, useId } from 'vue'
import { useMq } from 'vue3-mq'

import { type Placement } from '@floating-ui/vue'
import { OnClickOutside } from '@vueuse/components'

import { useFloatingAnchor } from '@/composables/floatingAnchor'

import PriceRuleExplainerCard from '@/components/PriceRule/PriceRuleExplainerCard.vue'

import type { FlatContentfulEntry } from '#contentful/ContentfulEntrySys'
import type { ContentfulPriceRuleSprite } from '#contentful/ContentfulPriceRuleSprite'

/** The surface a price-rule sprite renders on; governs icon / label / popover treatment. */
export enum PriceRuleSpriteContext {
  AppHeader = 'app-header',
  BundleItem = 'bundle-item'
}

/** Delay before the app-header sprite auto-opens its popover on load. */
const AUTO_SHOW_DELAY_MS = 500

/**
 * Promotional badge for an active Sunny16 price rule. Renders the Contentful-authored
 * sprite (icon + label) using its own foreground/background colors. Used for both the
 * app-header sprite and the per-bundle sprite — the surfaces differ only in placement.
 *
 * When the sprite has `extendedDescription` markdown, the badge gains a hover popover
 * (desktop only) that renders that copy — e.g. the app-header credit explainer.
 */
export default defineComponent({
  name: 'PriceRuleSprite',
  components: {
    OnClickOutside,
    PriceRuleExplainerCard
  },
  props: {
    sprite: {
      type: Object as PropType<FlatContentfulEntry<ContentfulPriceRuleSprite>>,
      required: true
    },
    context: {
      type: String as PropType<PriceRuleSpriteContext>,
      required: true
    }
  },
  setup() {
    // setup is reserved for composables; all state and behavior lives in the Options API below.
    const triggerEl = ref<HTMLElement | null>(null)
    const cardEl = ref<HTMLElement | null>(null)
    const { floatingStyles } = useFloatingAnchor(
      triggerEl,
      cardEl,
      ref('bottom-end' as Placement),
      ref(8)
    )

    return { cardEl, cardId: useId(), floatingStyles, mq$: useMq(), triggerEl }
  },
  data() {
    return {
      isOpen: false,
      autoShowTimer: undefined as ReturnType<typeof setTimeout> | undefined
    }
  },
  mounted() {
    if (!this.autoShowApplies) return
    // Auto-open the popover shortly after load, then dismiss on scroll. Outside-click dismissal is
    // handled by the <OnClickOutside> wrapper in the template.
    this.autoShowTimer = setTimeout(() => {
      this.isOpen = true
    }, AUTO_SHOW_DELAY_MS)
    window.addEventListener('scroll', this.dismiss, { passive: true })
  },
  beforeUnmount() {
    clearTimeout(this.autoShowTimer)
    window.removeEventListener('scroll', this.dismiss)
  },
  computed: {
    // Only the app-header sprite owns a popover (the credit explainer), so it's the only surface
    // that auto-opens on load.
    autoShowApplies(): boolean {
      return (
        this.context === PriceRuleSpriteContext.AppHeader &&
        !!this.sprite.extendedDescription?.trim()
      )
    },
    // `isOpen` tracks intent; `showTooltip` gates it, so the auto-open on load stays a no-op on the
    // surfaces that own no popover (mobile, and the per-bundle sprite).
    isCardVisible(): boolean {
      return this.isOpen && this.showTooltip
    },
    rootStyle(): Record<string, string> {
      return {
        backgroundColor: this.sprite.backgroundColor,
        color: this.sprite.textColor
      }
    },
    isAppHeader(): boolean {
      return this.context === PriceRuleSpriteContext.AppHeader
    },
    // The app-header pill drops its icon on mobile (per design); other surfaces keep it when present.
    showIcon(): boolean {
      return !!this.sprite.icon && (!this.isAppHeader || this.mq$.lgPlus)
    },
    // The app-header pill uses the shortened mobile label below lg; other surfaces use the full label.
    displayLabel(): string {
      if (this.isAppHeader && !this.mq$.lgPlus && this.sprite.labelMobile?.trim()) {
        return this.sprite.labelMobile
      }
      return this.sprite.label
    },
    hasExtendedDescription(): boolean {
      return !!this.sprite.extendedDescription?.trim()
    },
    // Hover popover is an app-header desktop affordance only.
    showTooltip(): boolean {
      return this.isAppHeader && this.hasExtendedDescription && this.mq$.lgPlus
    }
  },
  methods: {
    dismiss() {
      this.isOpen = false
    },
    onHover(entering: boolean) {
      if (!this.showTooltip) return
      this.isOpen = entering
    }
  }
})
</script>

<style scoped lang="scss">
.price-rule-sprite {
  // Matches the Figma "Sprite" component (node 10152:24359): a 4px-rounded rect,
  // 2px/6px padding, 4px gap, Roboto Bold 14/21. Colors come from Contentful per sprite.
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem; // 4px
  padding: 0.125rem 0.375rem; // 2px 6px
  border-radius: 0.25rem; // 4px
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  font-size: 0.875rem; // 14px
  line-height: 1.5; // 21px

  &__icon {
    height: 1em; // ~text height, matching the inline icon scale in the design
    width: auto;
  }

  &__label {
    white-space: nowrap;
  }

  // Signals the hover popover is available (desktop, when extendedDescription is present).
  &--interactive {
    cursor: pointer;
  }
}
</style>

<style lang="scss">
// Teleported to `body`, so it sits outside this component's scoped-style scope. The width is pinned
// to match the Figma explainer rather than collapsing to the content. Desktop-only, so 340px is safe.
.price-rule-sprite-card {
  z-index: 1080; // Bootstrap's tooltip layer.
  width: 340px;
  max-width: calc(100vw - 2rem);
  padding: 1.5rem;
  border-radius: 12px;
  background-color: #fff;
  box-shadow: 0 8px 30px -6px rgba(0, 0, 0, 0.25);
}
</style>
